/**
 * Enhanced Session Manager - Advanced frontend authentication management
 * 
 * Features:
 * - Multi-session support with device tracking
 * - Automatic token refresh with rotation detection
 * - Enhanced security monitoring and risk assessment
 * - Multi-tab synchronization with conflict resolution
 * - Persistent sessions with secure storage
 * - Comprehensive error handling and recovery
 * - Device fingerprinting and anomaly detection
 */

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  sessionId: string;
  tokenType: string;
}

interface UserData {
  id: string;
  email: string;
  displayName: string;
  profilePicture?: string;
  [key: string]: any;
}

interface SessionData {
  tokens: TokenPair;
  user: UserData;
  deviceId: string;
  deviceFingerprint: string;
  createdAt: number;
  lastActivity: number;
  sessionInfo?: {
    deviceInfo?: any;
    isNewUser?: boolean;
    timestamp?: string;
  };
  securityInfo?: {
    activeSessions?: number;
    maxSessions?: number;
    accountLocked?: boolean;
    riskScore?: number;
    lastActivity?: string;
  };
}

interface DeviceInfo {
  userAgent: string;
  platform: string;
  browser: string;
  screen: string;
  timezone: string;
  language: string;
  timestamp: string;
  colorDepth?: number;
  pixelDepth?: number;
  cookieEnabled?: boolean;
  onlineStatus?: boolean;
  hardwareConcurrency?: number;
}

class EnhancedSessionManager {
  private static instance: EnhancedSessionManager;
  
  // Storage keys
  private readonly SESSION_KEY = 'fashion_app_enhanced_session';
  private readonly DEVICE_ID_KEY = 'fashion_app_enhanced_device_id';
  private readonly DEVICE_FINGERPRINT_KEY = 'fashion_app_device_fingerprint';
  
  // Configuration
  private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly ACTIVITY_UPDATE_INTERVAL = 60 * 1000; // 1 minute
  private readonly MAX_REFRESH_ATTEMPTS = 3;
  private readonly SECURITY_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
  
  // State management
  private refreshPromise: Promise<TokenPair> | null = null;
  private refreshAttempts: number = 0;
  private refreshFailureCount: number = 0;
  private lastRefreshFailure: number = 0;
  private readonly MAX_REFRESH_FAILURES = 3;
  private readonly REFRESH_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
  private isRefreshBlocked: boolean = false;
  private activityTimer: NodeJS.Timeout | null = null;
  private securityTimer: NodeJS.Timeout | null = null;
  private storageListener: ((e: StorageEvent) => void) | null = null;
  
  // Event callbacks
  private onSessionExpired: (() => void) | null = null;
  private onTokenRefreshed: ((tokens: TokenPair) => void) | null = null;
  private onSecurityViolation: ((details: any) => void) | null = null;
  private onAccountLocked: ((details: any) => void) | null = null;
  private onLogout: (() => void) | null = null;
  
  private constructor() {
    this.initializeDeviceFingerprint();
    this.setupStorageListener();
    this.startActivityTimer();
    this.startSecurityMonitoring();
  }
  
  public static getInstance(): EnhancedSessionManager {
    if (!EnhancedSessionManager.instance) {
      EnhancedSessionManager.instance = new EnhancedSessionManager();
    }
    return EnhancedSessionManager.instance;
  }
  
  /**
   * Initialize or get device fingerprint for enhanced security
   */
  private initializeDeviceFingerprint(): string {
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
    let fingerprint = localStorage.getItem(this.DEVICE_FINGERPRINT_KEY);
    
    if (!deviceId || !fingerprint) {
      const deviceInfo = this.getDeviceInfo();
      deviceId = this.generateDeviceId(deviceInfo);
      fingerprint = this.generateDeviceFingerprint(deviceInfo);
      
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
      localStorage.setItem(this.DEVICE_FINGERPRINT_KEY, fingerprint);
    }
    
    return fingerprint;
  }
  
  /**
   * Generate unique device ID based on browser characteristics
   */
  private generateDeviceId(deviceInfo: DeviceInfo): string {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2);
    const userAgent = deviceInfo.userAgent.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
    const screenSize = deviceInfo.screen;
    const timezone = deviceInfo.timezone;
    
    const fingerprint = `${userAgent}-${screenSize}-${timezone}`;
    const hash = this.simpleHash(fingerprint);
    
    return `dev_${timestamp}_${randomStr}_${hash}`;
  }
  
  /**
   * Generate device fingerprint for security monitoring
   */
  private generateDeviceFingerprint(deviceInfo: DeviceInfo): string {
    const fingerprintData = {
      userAgent: deviceInfo.userAgent,
      platform: deviceInfo.platform,
      browser: deviceInfo.browser,
      screen: deviceInfo.screen,
      timezone: deviceInfo.timezone,
      language: deviceInfo.language,
      colorDepth: deviceInfo.colorDepth || 0,
      pixelDepth: deviceInfo.pixelDepth || 0,
      cookieEnabled: deviceInfo.cookieEnabled || false,
      hardwareConcurrency: deviceInfo.hardwareConcurrency || 1
    };
    
    const fingerprintString = JSON.stringify(fingerprintData);
    return this.simpleHash(fingerprintString);
  }
  
  /**
   * Simple hash function for fingerprinting
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }
  
  /**
   * Get comprehensive device information
   */
  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    let platform = 'Unknown';
    let browser = 'Unknown';
    
    // Detect platform
    if (userAgent.includes('Windows')) platform = 'Windows';
    else if (userAgent.includes('Mac')) platform = 'macOS';
    else if (userAgent.includes('Linux')) platform = 'Linux';
    else if (userAgent.includes('Android')) platform = 'Android';
    else if (userAgent.includes('iOS')) platform = 'iOS';
    
    // Detect browser
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    return {
      userAgent,
      platform,
      browser,
      screen: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      cookieEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      hardwareConcurrency: navigator.hardwareConcurrency || 1
    };
  }
  
  /**
   * Setup localStorage event listener for multi-tab synchronization
   */
  private setupStorageListener(): void {
    this.storageListener = (e: StorageEvent) => {
      if (e.key === this.SESSION_KEY) {
        console.log('🔄 Enhanced session updated in another tab, synchronizing...');
        
        if (e.newValue === null) {
          // Session was cleared in another tab
          this.handleSessionCleared();
        } else {
          // Session was updated in another tab
          this.handleSessionUpdated();
        }
      }
    };
    
    window.addEventListener('storage', this.storageListener);
  }
  
  /**
   * Start activity timer to update last activity
   */
  private startActivityTimer(): void {
    this.activityTimer = setInterval(() => {
      const session = this.getStoredSession();
      if (session) {
        session.lastActivity = Date.now();
        this.storeSession(session);
      }
    }, this.ACTIVITY_UPDATE_INTERVAL);
  }
  
  /**
   * Start security monitoring
   */
  private startSecurityMonitoring(): void {
    this.securityTimer = setInterval(() => {
      this.performSecurityCheck();
    }, this.SECURITY_CHECK_INTERVAL);
  }
  
  /**
   * Perform security check
   */
  private performSecurityCheck(): void {
    const session = this.getStoredSession();
    if (!session) return;
    
    // Check for device fingerprint changes
    const currentFingerprint = this.generateDeviceFingerprint(this.getDeviceInfo());
    if (session.deviceFingerprint !== currentFingerprint) {
      console.warn('🚨 Device fingerprint changed, potential security issue');
      
      if (this.onSecurityViolation) {
        this.onSecurityViolation({
          type: 'device_fingerprint_change',
          oldFingerprint: session.deviceFingerprint,
          newFingerprint: currentFingerprint,
          timestamp: new Date().toISOString()
        });
      }
      
      // Update fingerprint but log the change
      session.deviceFingerprint = currentFingerprint;
      this.storeSession(session);
    }
    
    // Check for suspicious activity patterns
    const timeSinceLastActivity = Date.now() - session.lastActivity;
    if (timeSinceLastActivity > 24 * 60 * 60 * 1000) { // 24 hours
      console.warn('🚨 Long period of inactivity detected');
    }
  }
  
  /**
   * Handle session cleared in another tab
   */
  private handleSessionCleared(): void {
    this.onSessionExpired?.();
  }
  
  /**
   * Handle session updated in another tab
   */
  private handleSessionUpdated(): void {
    const session = this.getStoredSession();
    if (session && this.onTokenRefreshed) {
      this.onTokenRefreshed(session.tokens);
    }
  }
  
  /**
   * Set event callbacks
   */
  public setEventCallbacks(callbacks: {
    onSessionExpired?: () => void;
    onTokenRefreshed?: (tokens: TokenPair) => void;
    onSecurityViolation?: (details: any) => void;
    onAccountLocked?: (details: any) => void;
  }): void {
    this.onSessionExpired = callbacks.onSessionExpired || null;
    this.onTokenRefreshed = callbacks.onTokenRefreshed || null;
    this.onSecurityViolation = callbacks.onSecurityViolation || null;
    this.onAccountLocked = callbacks.onAccountLocked || null;
  }
  
  /**
   * Create a new enhanced session
   */
  public async createSession(
    accessToken: string,
    refreshToken: string,
    user: UserData,
    sessionId: string,
    expiresIn: number = 15 * 60, // 15 minutes default
    additionalInfo?: any
  ): Promise<void> {
    try {
      const deviceId = localStorage.getItem(this.DEVICE_ID_KEY) || this.generateDeviceId(this.getDeviceInfo());
      const deviceFingerprint = this.initializeDeviceFingerprint();
      const now = Date.now();
      const expiresAt = now + (expiresIn * 1000);
      
      const sessionData: SessionData = {
        tokens: {
          accessToken,
          refreshToken,
          expiresAt,
          sessionId,
          tokenType: additionalInfo?.tokenType || 'Bearer'
        },
        user,
        deviceId,
        deviceFingerprint,
        createdAt: now,
        lastActivity: now,
        sessionInfo: additionalInfo,
        securityInfo: {
          activeSessions: 1,
          maxSessions: 10,
          accountLocked: false,
          riskScore: 0,
          lastActivity: new Date().toISOString()
        }
      };
      
      this.storeSession(sessionData);
      this.refreshAttempts = 0; // Reset refresh attempts on new session
      
      console.log('✅ Enhanced session created', {
        userId: user.id,
        sessionId,
        deviceId,
        deviceFingerprint,
        expiresAt: new Date(expiresAt),
        additionalInfo
      });
      
    } catch (error) {
      console.error('❌ Failed to create enhanced session:', error);
      throw new Error('Enhanced session creation failed');
    }
  }
  
  /**
   * Get current access token with enhanced automatic refresh and circuit breaker
   */
  public async getAccessToken(): Promise<string | null> {
    try {
      const session = this.getStoredSession();
      if (!session) {
        return null;
      }
      
      // Validate token format first
      const token = session.tokens.accessToken;
      if (typeof token !== 'string') {
        console.error('❌ EnhancedSessionManager: Token is not a string:', typeof token);
        this.clearSession();
        return null;
      }
      
      // Check if current token is still valid (not expired)
      const now = Date.now();
      const isTokenExpired = now >= session.tokens.expiresAt;
      
      // If token is still valid, return it immediately
      if (!isTokenExpired) {
        // Only attempt refresh if token expires within 2 minutes (reduced from 5 minutes)
        const needsRefresh = now > (session.tokens.expiresAt - (2 * 60 * 1000));
        
        if (!needsRefresh) {
          return token;
        }
        
        // Token needs refresh but is still valid - attempt refresh in background
        if (!this.refreshPromise) {
          console.log('🔄 Enhanced access token needs refresh (background), refreshing...');
          
          // Start refresh but don't wait for it - return current valid token
          this.refreshTokens().then(() => {
            console.log('✅ Background token refresh completed');
          }).catch((refreshError: any) => {
            console.warn('⚠️ Background token refresh failed:', refreshError);
            // Don't increment failure counts for background refresh
          });
        }
        
        // Return current valid token while refresh happens in background
        return token;
      }
      
      // Token is expired - we need to refresh synchronously
      console.log('🔄 Enhanced access token expired, refreshing synchronously...');
      
      // Circuit breaker: Check if refresh is blocked due to repeated failures
      if (this.isRefreshBlocked) {
        const timeSinceLastFailure = Date.now() - this.lastRefreshFailure;
        if (timeSinceLastFailure < this.REFRESH_COOLDOWN_MS) {
          console.log('🚫 Refresh blocked due to repeated failures, cooldown active');
          // Don't clear session immediately - let user try to re-authenticate
          return null;
        } else {
          // Reset circuit breaker after cooldown
          this.isRefreshBlocked = false;
          this.refreshFailureCount = 0;
          console.log('🔄 Refresh cooldown expired, resetting circuit breaker');
        }
      }
      
      // If refresh is already in progress, wait for it
      if (this.refreshPromise) {
        try {
          const newTokens = await this.refreshPromise;
          return newTokens.accessToken;
        } catch (error) {
          console.error('❌ Waiting for enhanced token refresh failed:', error);
          // Don't return null immediately - check if we can still use current token
          if (!isTokenExpired) {
            console.log('🔄 Refresh failed but current token still valid, using it');
            return token;
          }
          return null;
        }
      }
      
      // Attempt synchronous refresh for expired token
      try {
        const newTokens = await this.refreshTokens();
        if (newTokens) {
          // Reset failure counts on successful refresh
          this.refreshAttempts = 0;
          this.refreshFailureCount = 0;
          this.isRefreshBlocked = false;
          return newTokens.accessToken;
        }
      } catch (refreshError: any) {
        console.error('❌ Enhanced token refresh failed:', refreshError);
        
        // Increment failure counts
        this.refreshAttempts++;
        this.refreshFailureCount++;
        this.lastRefreshFailure = Date.now();
        
        // Handle specific error types that require immediate session clearing
        if (refreshError.message.includes('revoked') || 
            refreshError.message.includes('theft') ||
            refreshError.message.includes('SECURITY_VIOLATION')) {
          if (this.onSecurityViolation) {
            this.onSecurityViolation({
              type: 'token_security_violation',
              message: refreshError.message,
              timestamp: new Date().toISOString()
            });
          }
          this.clearSession();
          return null;
    // Handle session format compatibility issues
    if (refreshError.message.includes('Invalid refresh token') || 
        refreshError.message.includes('Session is no longer active') ||
        refreshError.message.includes('Invalid token type')) {
      console.log('🔄 Session format compatibility issue detected - clearing session for re-authentication');
      this.clearSession();
      
      // Notify session expired callback to trigger re-authentication
      this.onSessionExpired?.();
      
      return null;
    }
    // Handle specific JWT validation errors
    if (refreshError.message.includes('jwt audience invalid') || 
        refreshError.message.includes('jwt issuer invalid')) {
      console.log('🔄 JWT validation mismatch detected - clearing session for re-authentication');
      this.clearSession();
      
      // Notify session expired callback to trigger re-authentication
      this.onSessionExpired?.();
      
      return null;
    }
        }
        
        // Activate circuit breaker if too many failures (increased threshold)
        if (this.refreshFailureCount >= 5) { // Increased from 3 to 5
          console.error('🚫 Too many refresh failures, activating circuit breaker');
          this.isRefreshBlocked = true;
          
          // Don't clear session immediately - give user chance to re-authenticate
          console.log('⚠️ Circuit breaker activated - user will need to re-authenticate');
          
          // Notify session expired callback
          this.onSessionExpired?.();
          
          return null;
        }
        
        // For temporary failures, check if current token is still usable
        if (!isTokenExpired) {
          console.log('🔄 Refresh failed but current token still valid, using it');
          return token;
        }
        
        // Token is expired and refresh failed - return null
        return null;
      }
      
      // Fallback - return current token if available
      return token;
      
    } catch (error) {
      console.error('❌ Failed to get enhanced access token:', error);
      
      // Try to return current token as fallback
      try {
        const session = this.getStoredSession();
        if (session && session.tokens && session.tokens.accessToken) {
          const now = Date.now();
          const isTokenExpired = now >= session.tokens.expiresAt;
          
          if (!isTokenExpired) {
            console.log('🔄 Error in getAccessToken, but returning valid current token');
            return session.tokens.accessToken;
          }
        }
      } catch (fallbackError) {
        console.error('❌ Fallback token retrieval failed:', fallbackError);
      }
      
      return null;
    }
  }
  
  /**
   * Refresh access token with enhanced security
   */
  private async refreshTokens(): Promise<TokenPair> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    
    this.refreshPromise = this.performEnhancedTokenRefresh();
    
    try {
      const newTokens = await this.refreshPromise;
      return newTokens;
    } finally {
      this.refreshPromise = null;
    }
  }
  
  /**
   * Perform the actual enhanced token refresh API call
   */
  private async performEnhancedTokenRefresh(): Promise<TokenPair> {
    const session = this.getStoredSession();
    if (!session) {
      throw new Error('No session available for refresh');
    }
    
    // Check if we're already in a refresh loop
    if (this.refreshFailureCount >= this.MAX_REFRESH_FAILURES) {
      console.log('🚫 Too many refresh failures, clearing session and redirecting to login');
      this.clearSession();
      this.triggerLogout();
      throw new Error('Too many refresh failures - session cleared');
    }
    
    try {
      const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: session.tokens.refreshToken,
          deviceId: session.deviceId,
          deviceInfo: this.getDeviceInfo()
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific error responses
        if (response.status === 401) {
          console.log('🚫 Refresh token is invalid or expired, clearing session');
          await this.clearSession();
          this.triggerLogout();
          throw new Error('Refresh token expired - session cleared');
        } else if (response.status === 403 && errorData.error?.code === 'SECURITY_VIOLATION') {
          throw new Error('Security violation detected - all sessions terminated for safety');
        } else if (response.status === 423) {
          if (this.onAccountLocked) {
            this.onAccountLocked(errorData);
          }
          throw new Error('Account is temporarily locked');
        }
        
        throw new Error(`Enhanced token refresh failed: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }
      
      // Reset failure count on success
      this.refreshFailureCount = 0;
      
      const data = await response.json();
      
      if (!data.accessToken || !data.refreshToken) {
        throw new Error('Invalid enhanced refresh response');
      }
      
      const newTokens: TokenPair = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: Date.now() + (data.expiresIn * 1000),
        sessionId: data.sessionId || session.tokens.sessionId,
        tokenType: data.tokenType || session.tokens.tokenType
      };
      
      // Update stored session with new tokens
      session.tokens = newTokens;
      session.lastActivity = Date.now();
      
      // Update security info if provided
      if (data.security) {
        session.securityInfo = {
          ...session.securityInfo,
          ...data.security,
          lastActivity: new Date().toISOString()
        };
      }
      
      this.storeSession(session);
      
      // Notify callbacks
      if (this.onTokenRefreshed) {
        this.onTokenRefreshed(newTokens);
      }
      
      console.log('✅ Enhanced tokens refreshed successfully', {
        sessionId: newTokens.sessionId,
        security: data.security
      });
      
      return newTokens;
      
        } catch (error: any) {
      console.error('❌ Enhanced token refresh failed:', error);
      
      // Increment failure count
      this.refreshFailureCount++;
      
      // If it's a 401 error, clear the session immediately
      if (error.message.includes('401') || error.message.includes('expired') || error.message.includes('invalid')) {
        console.log('🧹 Clearing session due to authentication failure');
        this.clearSession(); // Remove await since clearSession is not async
        this.triggerLogout();
      }
      
      throw error;
    }
  }
  
  /**
   * Get current user data
   */
  public getUser(): UserData | null {
    const session = this.getStoredSession();
    return session?.user || null;
  }
  
  /**
   * Get comprehensive session info
   */
  public getSessionInfo(): any {
    const session = this.getStoredSession();
    if (!session) return null;
    
    return {
      userId: session.user.id,
      sessionId: session.tokens.sessionId,
      deviceId: session.deviceId,
      deviceFingerprint: session.deviceFingerprint,
      createdAt: new Date(session.createdAt),
      lastActivity: new Date(session.lastActivity),
      tokenExpiresAt: new Date(session.tokens.expiresAt),
      tokenType: session.tokens.tokenType,
      needsRefresh: Date.now() > (session.tokens.expiresAt - this.REFRESH_BUFFER_MS),
      refreshAttempts: this.refreshAttempts,
      ...session.sessionInfo
    };
  }
  
  /**
   * Get security information
   */
  public getSecurityInfo(): any {
    const session = this.getStoredSession();
    return session?.securityInfo || null;
  }
  
  /**
   * Update session info
   */
  public updateSessionInfo(info: any): void {
    const session = this.getStoredSession();
    if (session) {
      session.sessionInfo = { ...session.sessionInfo, ...info };
      session.lastActivity = Date.now();
      this.storeSession(session);
    }
  }
  
  /**
   * Check if user is logged in
   */
  public async isLoggedIn(): Promise<boolean> {
    try {
      const token = await this.getAccessToken();
      return token !== null;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Logout from current session
   */
  public async logout(): Promise<void> {
    try {
      const session = this.getStoredSession();
      if (session) {
        // Notify backend to revoke session
        try {
          await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.tokens.accessToken}`
            },
            body: JSON.stringify({
              sessionId: session.tokens.sessionId
            })
          });
        } catch (error) {
          console.warn('Backend enhanced logout call failed:', error);
          // Continue with local logout even if backend call fails
        }
      }
    } catch (error) {
      console.error('Enhanced logout error:', error);
    } finally {
      this.clearSession();
    }
  }
  
  /**
   * Logout from all sessions (all devices)
   */
  public async logoutAllSessions(): Promise<void> {
    try {
      const session = this.getStoredSession();
      if (session) {
        try {
          await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/logout-all`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.tokens.accessToken}`
            }
          });
        } catch (error) {
          console.warn('Backend enhanced logout-all call failed:', error);
        }
      }
    } catch (error) {
      console.error('Enhanced logout all error:', error);
    } finally {
      this.clearSession();
    }
  }
  
  /**
   * Update user data in session
   */
  public updateUser(userData: Partial<UserData>): void {
    const session = this.getStoredSession();
    if (session) {
      console.log('🔄 EnhancedSessionManager: Updating user data', {
        existingFields: Object.keys(session.user),
        newFields: Object.keys(userData),
        tryOnProfilePictureUrl: userData.tryOnProfilePictureUrl,
        hasTryOnPicture: !!userData.tryOnProfilePictureUrl
      });
      
      session.user = { ...session.user, ...userData };
      session.lastActivity = Date.now();
      this.storeSession(session);
      
      console.log('✅ EnhancedSessionManager: User data updated and stored', {
        updatedFields: Object.keys(session.user),
        tryOnProfilePictureUrl: session.user.tryOnProfilePictureUrl,
        hasTryOnPicture: !!session.user.tryOnProfilePictureUrl
      });
    } else {
      console.warn('⚠️ EnhancedSessionManager: No session to update');
    }
  }

  /**
   * Set session with token and user data (compatibility method)
   */
  public setSession(token: string, user: UserData, expiresIn: number = 3600): void {
    const sessionId = `session-${Date.now()}`;
    
    this.createSession(token, token, user, sessionId, expiresIn, {
      tokenType: 'Bearer',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Set refresh token (compatibility method)
   */
  public setRefreshToken(refreshToken: string): void {
    const session = this.getStoredSession();
    if (session) {
      session.tokens.refreshToken = refreshToken;
      this.storeSession(session);
    }
  }

  /**
   * Set session ID (compatibility method)
   */
  public setSessionId(sessionId: string): void {
    const session = this.getStoredSession();
    if (session) {
      session.tokens.sessionId = sessionId;
      this.storeSession(session);
    }
  }

  /**
   * Restore session (compatibility method)
   */
  public async restoreSession(): Promise<{ token: string; user: UserData } | null> {
    const session = this.getStoredSession();
    if (session && session.tokens.accessToken) {
      return {
        token: session.tokens.accessToken,
        user: session.user
      };
    }
    return null;
  }
  
  /**
   * Store session data in localStorage with encryption consideration
   */
  private storeSession(sessionData: SessionData): void {
    try {
      // In a production environment, consider encrypting sensitive data
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.error('Failed to store enhanced session:', error);
      throw new Error('Enhanced session storage failed');
    }
  }
  
  /**
   * Get stored session data from localStorage
   */
  private getStoredSession(): SessionData | null {
    try {
      const sessionStr = localStorage.getItem(this.SESSION_KEY);
      if (!sessionStr) return null;
      
      const session: SessionData = JSON.parse(sessionStr);
      
      // Check if session is expired
      if (Date.now() > session.tokens.expiresAt) {
        // Don't auto-clear here, let the refresh mechanism handle it
        console.log('ℹ️ Session token expired, refresh will be attempted');
      }
      
      return session;
      
    } catch (error) {
      console.error('Failed to get stored enhanced session:', error);
      this.clearSession();
      return null;
    }
  }
  
  /**
   * Clear session data and cleanup
   */
  public clearSession(): void {
    try {
      localStorage.removeItem(this.SESSION_KEY);
      
      // Clear timers
      if (this.activityTimer) {
        clearInterval(this.activityTimer);
        this.activityTimer = null;
      }
      
      if (this.securityTimer) {
        clearInterval(this.securityTimer);
        this.securityTimer = null;
      }
      
      // Reset all state including circuit breaker
      this.refreshPromise = null;
      this.refreshAttempts = 0;
      this.refreshFailureCount = 0;
      this.lastRefreshFailure = 0;
      this.isRefreshBlocked = false;
      
      console.log('🗑️ Enhanced session cleared and circuit breaker reset');
      
    } catch (error) {
      console.error('Failed to clear enhanced session:', error);
    }
  }
  
  /**
   * Cleanup resources
   */
  public destroy(): void {
    // Remove event listeners
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
    
    // Clear timers
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
    
    if (this.securityTimer) {
      clearInterval(this.securityTimer);
      this.securityTimer = null;
    }
    
    // Clear promises and state
    this.refreshPromise = null;
    this.refreshAttempts = 0;
    
    // Clear callbacks
    this.onSessionExpired = null;
    this.onTokenRefreshed = null;
    this.onSecurityViolation = null;
    this.onAccountLocked = null;
  }

  /**
   * Trigger logout and redirect to login page
   */
  private triggerLogout(): void {
    console.log('🚪 Triggering logout due to authentication failure');
    
    // Clear all storage
    this.clearAllStorage();
    
    // Trigger logout event
    if (this.onLogout) {
      this.onLogout();
    }
    
    // Redirect to login page if we're not already there
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
      console.log('🔄 Redirecting to login page');
      window.location.href = '/login?reason=session_expired';
    }
  }

  /**
   * Clear all authentication-related storage
   */
  private clearAllStorage(): void {
    try {
      if (typeof window !== 'undefined') {
        // Clear localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('auth') || key.includes('session') || key.includes('token'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Clear sessionStorage
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.includes('auth') || key.includes('session') || key.includes('token'))) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
        
        console.log('🧹 Cleared authentication storage');
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  /**
   * Set logout callback
   */
  public setOnLogout(callback: () => void): void {
    this.onLogout = callback;
  }
}

// Export singleton instance
export default EnhancedSessionManager.getInstance();